import { useState, useMemo } from 'react'
import StarRating from 'src/components/StarRating'
import { Statsig } from 'src/sdk/statsig'
import {
  DESCRIPTION_EXPERIMENT_ID,
  REVIEWS_EXPERIMENT_ID,
} from 'src/components/common/StatsigExposure/StatsigExposure'
import { SsrOnlyContent } from 'src/components/common/SsrOnlyContent/SsrOnlyContent'
import { SELLER_DECATHLON } from 'src/constants'

import { useProductContextSelector } from '../../contexts'
import ExpandSection from './commonComponents/ExpandSection'
import { CareSymbol } from './careSymbol'
import { EnvironmentalData } from './EnvironmentalData'
import { TechnicalInformation } from './TechnicalInformation'
import CustomerReviewsVersionB from '../CustomerReviews/CustomerReviewsVersionB'
import { ProductDescription } from './ProductDescription'
import { ProductDescriptionCarousel } from './ProductDescriptionCarousel'
import SsrReviewsSnapshot from './SsrReviewsSnapshot/SsrReviewsSnapshot'

export type WhatOpenSection =
  | 'descricao'
  | 'care'
  | 'characteristics'
  | 'tecnical'
  | 'environmental'

type ProductObjectType = Array<{
  titleProductInformation: React.ReactNode
  sectionName: string
  eventName: string
  sectionContent: React.ReactNode
  ssrOnlyContent?: React.ReactNode
  modalTitle?: string
}>

interface ProductInformationSectionProps {
  isAlbum?: boolean
}

export function ProductInformationSection({
  isAlbum = false,
}: ProductInformationSectionProps) {
  const product = useProductContextSelector((ctx) => ctx.product)
  const currentSku = useProductContextSelector((ctx) => ctx.currentSku)

  const {
    sellerDefault: { sellerId },
  } = currentSku

  const { review } = product

  const isReviewDisplayExperimentEnabled = Statsig.useStatsigFeatureGateValue(
    REVIEWS_EXPERIMENT_ID
  )

  const isDescriptionExperimentEnabled = Statsig.useStatsigFeatureGateValue(
    DESCRIPTION_EXPERIMENT_ID
  )

  const { technicalInformation, productDocuments, productBenefits } = currentSku
  const { careSymbol, careInstruction, environmentalData, description } =
    product

  const careInstructionInfo = careInstruction?.[0]?.name

  const sellerIsDecathlon = sellerId === SELLER_DECATHLON

  const [whatOpenSection, setWhatOpenSection] = useState<
    WhatOpenSection | undefined
  >(undefined)

  const sectionObject = useMemo(() => {
    const sections: ProductObjectType = []

    if (isDescriptionExperimentEnabled && description !== '') {
      sections.push({
        titleProductInformation: 'Sobre o produto',
        sectionName: 'descricao',
        eventName: 'Sobre o produto',
        sectionContent: <ProductDescription contentOne={description} />,
        ssrOnlyContent: <ProductDescription contentOne={description} />,
      })
    }

    if (productDocuments || technicalInformation) {
      sections.push({
        titleProductInformation: 'Informações Técnicas',
        sectionName: 'technicalInformation',
        eventName: 'Informações Técnicas',
        sectionContent: (
          <TechnicalInformation
            contentOne={productDocuments}
            contentTwo={technicalInformation}
          />
        ),
        ssrOnlyContent: (
          <TechnicalInformation
            contentOne={productDocuments}
            contentTwo={technicalInformation}
          />
        ),
        modalTitle: 'Informações Técnicas',
      })
    }

    if (
      !isAlbum &&
      isReviewDisplayExperimentEnabled &&
      sellerIsDecathlon &&
      review
    ) {
      const TitleSection = () => (
        <span className="flex items-center gap-2">
          Avaliações{' '}
          <div className="flex items-center gap-1.5">
            <span>
              <StarRating
                rating={review?.totalRatingsAverageNote}
                width={11}
                height={11}
                maxStars={5}
                color="#101010"
              />
            </span>
            <span className="text-font-size-3 text-restructure-primary">
              {review?.totalRatingsAverageNote?.toFixed(1).replace('.', ',')}
            </span>
          </div>
        </span>
      )

      sections.push({
        titleProductInformation: <TitleSection />,
        sectionName: 'reviews',
        eventName: 'Avaliações',
        sectionContent: (
          <CustomerReviewsVersionB currentSku={currentSku} product={product} />
        ),
        modalTitle: 'Avaliações',
      })
    }

    if (
      (careSymbol && careSymbol.length !== 0) ||
      (careInstruction && careInstruction.length !== 0)
    ) {
      sections.push({
        titleProductInformation: 'Utilização e manutenção',
        sectionName: 'careSymbol',
        eventName: 'Utilizacao e manutencao',
        sectionContent: (
          <CareSymbol
            contentOne={careInstructionInfo}
            contentTwo={careSymbol}
          />
        ),
        modalTitle: 'Utilização e Manutenção',
      })
    }

    if (environmentalData && environmentalData.length !== 0) {
      sections.push({
        titleProductInformation: 'Impacto ambiental',
        sectionName: 'environmentalData',
        eventName: 'Impacto ambiental',
        sectionContent: <EnvironmentalData contentOne={environmentalData} />,
        modalTitle: 'Impacto ambiental',
      })
    }

    return sections
  }, [
    productDocuments,
    technicalInformation,
    isReviewDisplayExperimentEnabled,
    sellerIsDecathlon,
    review,
    careSymbol,
    careInstruction,
    environmentalData,
    currentSku,
    product,
    careInstructionInfo,
    description,
    isDescriptionExperimentEnabled,
    isAlbum,
  ])

  const shouldRenderSsrReviewsSnapshot =
    !isAlbum && sellerIsDecathlon && Boolean(review?.reviews?.length)

  if (!sectionObject || sectionObject.length === 0) {
    return null
  }

  return (
    <>
      {productBenefits && (
        <div>
          <ProductDescriptionCarousel contentOne={productBenefits} />
        </div>
      )}
      {sectionObject?.map((section) => (
        <ExpandSection
          key={section.sectionName}
          titleProductInformation={section.titleProductInformation}
          sectionName={section.sectionName}
          modalContent={section.sectionContent}
          ssrOnlyContent={section.ssrOnlyContent}
          whatOpenSection={whatOpenSection}
          setWhatOpenSection={setWhatOpenSection}
          eventName={section.eventName}
          modalTitle={section.modalTitle ?? ''}
        />
      ))}

      {shouldRenderSsrReviewsSnapshot && review && (
        <SsrOnlyContent sectionName="reviews" testId="ssr-only-content-reviews">
          <SsrReviewsSnapshot review={review} />
        </SsrOnlyContent>
      )}
    </>
  )
}
